// client/src/components/pages/LexiStar/TripDetail/RecordPanel.jsx
//
// The trip RECORD's lifecycle on the trip detail (LS-04 §4.1; T4-S13; M3-U1):
// OPEN → SIGNED_OFF → AMENDED, orthogonal to the stage.
//   Sign off     lexistar.trips.signoff; only once the trip is COMPLETED or
//                RECONCILED and has no open review items — the blocking items
//                are listed with links into the review queue. Locks timeline,
//                tickets, fuel and cost. POST /trips/:id:signOff.
//   Amendments   a signed-off trip changes only by amendment: requested with a
//                reason (POST /trips/:id:amend), approved or rejected by a
//                SECOND user with lexistar.trips.amend (the database rejects
//                approved_by = requested_by). Approval moves it to AMENDED.
//   Audit trail  GET /trips/:id/audit — the hash-chained ledger rows.

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaLock, FaPen, FaCheck, FaTimes, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { useAuth } from '../../../../context/AuthContext';
import {
  listReviewItems,
  listTripAmendments,
  signOffTrip,
  requestTripAmendment,
  approveTripAmendment,
  rejectTripAmendment,
  getTripAudit,
  pickList,
} from '../../../../services/lexistarAssurance';
import { cardClasses, inputClasses } from '../StatusBoard/atoms';
import { fmtDateTime, fmtKg, fmtNum } from '../StatusBoard/format';
import { STAGE_LABELS } from '../StatusBoard/statusLanguage';
import {
  RecordStateChip,
  Modal,
  ReasonField,
  Field,
  InlineError,
  ConfidenceChip,
  buttonClasses,
} from '../ReviewQueue/assuranceAtoms';
import {
  RECORD_STATE_META,
  SIGN_OFF_STAGES,
  errorMessage,
  isMe,
  kindLabel,
} from '../ReviewQueue/assuranceShared';

const CHANGE_LABELS = {
  dispatch_net_kg: 'Dispatch net',
  receipt_net_kg: 'Receipt net',
  driver_id: 'Driver',
  status_flags: 'Flags',
  'load.material': 'Material',
  'load.order_ref': 'Order reference',
  'load.planned_tonnes': 'Planned tonnes',
  'load.consignor': 'Consignor',
  'load.consignee': 'Consignee',
  correction_ids: 'Corrections',
};

/** { load: { material } , dispatch_net_kg } → [['load.material', v], ['dispatch_net_kg', v]] */
export function flattenChanges(changes) {
  const out = [];
  Object.entries(changes || {}).forEach(([k, v]) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      Object.entries(v).forEach(([k2, v2]) => out.push([`${k}.${k2}`, v2]));
    } else out.push([k, v]);
  });
  return out;
}

const valueAt = (trip, path) => path.split('.').reduce((o, k) => (o == null ? o : o[k]), trip);
const show = (path, v) =>
  v === null || v === undefined || v === ''
    ? '—'
    : path.endsWith('_kg')
      ? fmtKg(v)
      : Array.isArray(v)
        ? v.join(', ')
        : typeof v === 'number'
          ? fmtNum(v, v % 1 ? 1 : 0)
          : String(v);

/** Sign-off gate: { allowed, reasons[] }. */
export function signOffGate({ trip, openItems, canSignOff }) {
  const reasons = [];
  if (!canSignOff) reasons.push('Signing off needs the lexistar.trips.signoff permission.');
  if (!SIGN_OFF_STAGES.includes(trip?.stage))
    reasons.push(
      `The trip is ${STAGE_LABELS[trip?.stage]?.toLowerCase() || 'not finished'}; it can be signed off once it is completed or reconciled.`
    );
  if (openItems > 0)
    reasons.push(
      `${openItems} open review item${openItems === 1 ? '' : 's'} must be resolved first.`
    );
  return { allowed: reasons.length === 0, reasons };
}

function AmendmentForm({ trip, busy, error, onCancel, onSubmit }) {
  const [vals, setVals] = useState({
    dispatch_net_kg: trip.dispatch_net_kg ?? '',
    receipt_net_kg: trip.receipt_net_kg ?? '',
    material: trip.load?.material ?? '',
    order_ref: trip.load?.order_ref ?? '',
    planned_tonnes: trip.load?.planned_tonnes ?? '',
  });
  const [reason, setReason] = useState('');
  const [localError, setLocalError] = useState(null);
  const set = (k) => (e) => setVals((v) => ({ ...v, [k]: e.target.value }));

  const submit = () => {
    const changes = {};
    const num = (x) => (x === '' ? null : Number(x));
    for (const k of ['dispatch_net_kg', 'receipt_net_kg']) {
      if (String(vals[k]) !== String(trip[k] ?? '')) {
        const n = num(vals[k]);
        if (n !== null && (!Number.isFinite(n) || n < 0))
          return setLocalError('Weights must be zero or more kilograms.');
        changes[k] = n;
      }
    }
    const load = {};
    if (vals.material !== (trip.load?.material ?? '')) load.material = vals.material.trim() || null;
    if (vals.order_ref !== (trip.load?.order_ref ?? ''))
      load.order_ref = vals.order_ref.trim() || null;
    if (String(vals.planned_tonnes) !== String(trip.load?.planned_tonnes ?? ''))
      load.planned_tonnes = num(vals.planned_tonnes);
    if (Object.keys(load).length) changes.load = load;
    if (!Object.keys(changes).length) return setLocalError('Change at least one value.');
    if (!reason.trim()) return setLocalError('Give the reason for the amendment.');
    setLocalError(null);
    onSubmit({ reason: reason.trim(), changes });
  };

  return (
    <>
      <p className="text-sm text-slate-600">
        The trip is signed off. An amendment is applied only when a second authorised user approves
        it; afterwards every report that includes the trip carries an AMENDED watermark.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Dispatch net (kg)" htmlFor="amend-dispatch">
          <input
            id="amend-dispatch"
            type="number"
            min="0"
            className={inputClasses}
            value={vals.dispatch_net_kg}
            onChange={set('dispatch_net_kg')}
          />
        </Field>
        <Field label="Receipt net (kg)" htmlFor="amend-receipt">
          <input
            id="amend-receipt"
            type="number"
            min="0"
            className={inputClasses}
            value={vals.receipt_net_kg}
            onChange={set('receipt_net_kg')}
          />
        </Field>
        <Field label="Material" htmlFor="amend-material">
          <input
            id="amend-material"
            className={inputClasses}
            value={vals.material}
            onChange={set('material')}
          />
        </Field>
        <Field label="Order reference" htmlFor="amend-order">
          <input
            id="amend-order"
            className={inputClasses}
            value={vals.order_ref}
            onChange={set('order_ref')}
          />
        </Field>
        <Field label="Planned tonnes" htmlFor="amend-tonnes">
          <input
            id="amend-tonnes"
            type="number"
            min="0"
            step="0.1"
            className={inputClasses}
            value={vals.planned_tonnes}
            onChange={set('planned_tonnes')}
          />
        </Field>
      </div>
      <ReasonField
        value={reason}
        onChange={setReason}
        placeholder="Why the signed-off figures are wrong"
      />
      <InlineError message={localError || error} />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className={buttonClasses.secondary}>
          Cancel
        </button>
        <button type="button" onClick={submit} disabled={busy} className={buttonClasses.primary}>
          {busy ? 'Requesting…' : 'Request amendment'}
        </button>
      </div>
    </>
  );
}

export default function RecordPanel({ trip, amendmentsFromTrip, refreshKey, onChanged, onNotice }) {
  const { user, hasPermission } = useAuth();
  const canSignOff = hasPermission('lexistar.trips.signoff');
  const canAmend = hasPermission('lexistar.trips.amend');
  const state = trip.record_state || 'OPEN';
  const [openItems, setOpenItems] = useState(null); // null = unknown list
  const [amendments, setAmendments] = useState(amendmentsFromTrip || []);
  const [dialog, setDialog] = useState(null); // 'signoff' | 'amend' | {reject: amendment}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState(null);

  const load = useCallback(async () => {
    if (state === 'OPEN') {
      try {
        const res = await listReviewItems({ trip_id: trip.id, state: 'open' });
        setOpenItems(pickList(res, 'review_items', 'items'));
      } catch {
        setOpenItems(null);
      }
    } else setOpenItems([]);
    if (amendmentsFromTrip) setAmendments(amendmentsFromTrip);
    else {
      try {
        setAmendments(pickList(await listTripAmendments(trip.id), 'trip_amendments', 'amendments'));
      } catch {
        setAmendments([]);
      }
    }
  }, [trip.id, state, amendmentsFromTrip]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, refreshKey]);

  useEffect(() => {
    if (!auditOpen) return;
    getTripAudit(trip.id)
      .then((res) => setAudit(pickList(res, 'audit', 'entries', 'ledger')))
      .catch(() => setAudit([]));
  }, [auditOpen, trip.id, refreshKey]);

  const openCount = openItems ? openItems.length : Number(trip.open_review_items) || 0;
  const gate = signOffGate({ trip, openItems: openCount, canSignOff });
  const pending = amendments.filter((a) => (a.state || 'pending') === 'pending');
  const decided = amendments.filter((a) => a.state && a.state !== 'pending');

  const run = async (fn, message) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      onNotice?.(message);
      await onChanged?.();
      await load();
    } catch (err) {
      setError(errorMessage(err, 'The action failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`${cardClasses} p-5`} aria-label="Record">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            Record
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <RecordStateChip state={state} />
            {trip.plan_version > 1 && (
              <span className="text-xs font-medium text-slate-600 tabular-nums">
                plan version {trip.plan_version}
              </span>
            )}
          </div>
          <p className="mt-1.5 text-sm text-slate-600 max-w-prose">
            {RECORD_STATE_META[state]?.help}
          </p>
          {state !== 'OPEN' && trip.signed_off_at && (
            <p className="text-xs text-slate-500 mt-1">
              Signed off {trip.signed_off_by ? `by ${trip.signed_off_by} ` : ''}·{' '}
              {fmtDateTime(trip.signed_off_at)}
              {trip.amended_at && <> · amended {fmtDateTime(trip.amended_at)}</>}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {state === 'OPEN' && canSignOff && (
            <button
              type="button"
              onClick={() => {
                setError(null);
                setDialog('signoff');
              }}
              disabled={!gate.allowed}
              aria-describedby={!gate.allowed ? 'signoff-gate' : undefined}
              className={buttonClasses.primary}
            >
              <FaLock size={11} /> Sign off
            </button>
          )}
          {state !== 'OPEN' && canAmend && (
            <button
              type="button"
              onClick={() => {
                setError(null);
                setDialog('amend');
              }}
              className={buttonClasses.secondary}
            >
              <FaPen size={11} /> Request amendment…
            </button>
          )}
        </div>
      </div>

      {state === 'OPEN' && canSignOff && !gate.allowed && (
        <div id="signoff-gate" className="mt-3 text-sm text-slate-700 space-y-1">
          {gate.reasons.map((r) => (
            <p key={r}>• {r}</p>
          ))}
        </div>
      )}
      {state === 'OPEN' && openCount > 0 && (
        <div className="mt-3">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
            Blocking sign-off
          </h3>
          {openItems && openItems.length > 0 ? (
            <ul
              className="divide-y divide-slate-100 border border-slate-200 rounded-lg"
              aria-label="Open review items"
            >
              {openItems.map((i) => (
                <li
                  key={i.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <span className="min-w-0">
                    <span className="font-medium text-slate-900">{kindLabel(i.kind)}</span>{' '}
                    <span className="font-mono text-xs text-slate-500 break-all">
                      {i.subject_id}
                    </span>
                  </span>
                  <span className="flex items-center gap-2 flex-shrink-0">
                    <ConfidenceChip confidence={i.confidence} showLabel={false} />
                    <Link
                      to={`/lexistar/review?trip=${trip.id}&item=${i.id}`}
                      className="text-primary-700 hover:underline text-xs font-medium"
                    >
                      Review
                    </Link>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <Link
              to={`/lexistar/review?trip=${trip.id}`}
              className="text-sm text-primary-700 hover:underline"
            >
              Open this trip’s {openCount} review item{openCount === 1 ? '' : 's'}
            </Link>
          )}
        </div>
      )}

      {pending.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
            Amendment requests awaiting a second user
          </h3>
          <ul className="space-y-2" aria-label="Pending amendments">
            {pending.map((a) => {
              const mine = isMe(user, a.requested_by);
              return (
                <li key={a.id} className="border border-slate-200 rounded-lg p-3">
                  <p className="text-sm text-slate-900">
                    “{a.reason}”{' '}
                    <span className="text-xs text-slate-500">
                      — {a.requested_by} · {fmtDateTime(a.requested_at)}
                    </span>
                  </p>
                  <table className="mt-2 text-sm w-full" aria-label="Requested changes">
                    <tbody>
                      {flattenChanges(a.changes).map(([path, v]) => (
                        <tr key={path}>
                          <td className="py-0.5 pr-3 text-slate-500">
                            {CHANGE_LABELS[path] || path}
                          </td>
                          <td className="py-0.5 pr-3 text-slate-500 line-through tabular-nums">
                            {show(path, valueAt(trip, path))}
                          </td>
                          <td className="py-0.5 text-slate-900 font-semibold tabular-nums">
                            {show(path, v)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {canAmend && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          run(
                            () => approveTripAmendment(a.id),
                            'Amendment approved; the trip is now AMENDED.'
                          )
                        }
                        disabled={busy || mine}
                        className={buttonClasses.primary}
                      >
                        <FaCheck size={11} /> Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setError(null);
                          setRejectReason('');
                          setDialog({ reject: a });
                        }}
                        disabled={busy}
                        className={buttonClasses.secondary}
                      >
                        <FaTimes size={11} /> Reject
                      </button>
                      {mine && (
                        <span className="text-xs text-slate-600">
                          You requested it — a second authorised user must approve it.
                        </span>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {decided.length > 0 && (
        <p className="mt-3 text-xs text-slate-500">
          {decided
            .map(
              (a) =>
                `${a.state === 'approved' ? 'Approved' : 'Rejected'} ${fmtDateTime(a.approved_at || a.updated_at)}${a.approved_by ? ` by ${a.approved_by}` : ''}: “${a.reason}”`
            )
            .join(' · ')}
        </p>
      )}
      {!dialog && <InlineError message={error} />}

      <div className="mt-4">
        <button
          type="button"
          onClick={() => setAuditOpen((o) => !o)}
          aria-expanded={auditOpen}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-800"
        >
          {auditOpen ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
          Audit trail
        </button>
        {auditOpen && (
          <div className="mt-2">
            {audit === null ? (
              <p className="text-xs text-slate-500">Loading…</p>
            ) : audit.length === 0 ? (
              <p className="text-xs text-slate-500">No audit entries for this trip yet.</p>
            ) : (
              <ol className="text-xs space-y-1" aria-label="Audit trail">
                {audit.map((e) => (
                  <li key={e.id} className="flex flex-wrap gap-x-2">
                    <span className="tabular-nums text-slate-500">{fmtDateTime(e.at)}</span>
                    <span className="font-semibold text-slate-800">
                      {String(e.action).replace(/_/g, ' ')}
                    </span>
                    <span className="text-slate-600">
                      {e.actor}
                      {e.second_actor && ` with ${e.second_actor}`}
                    </span>
                    {e.hash && (
                      <span className="font-mono text-slate-400" title={e.hash}>
                        #{String(e.hash).slice(0, 10)}
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>

      <Modal
        open={dialog === 'signoff'}
        title={`Sign off ${trip.reference}`}
        onClose={() => setDialog(null)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setDialog(null)}
              className={buttonClasses.secondary}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => run(() => signOffTrip(trip.id), `Signed off ${trip.reference}.`)}
              disabled={busy}
              className={buttonClasses.primary}
            >
              <FaLock size={11} /> {busy ? 'Signing off…' : 'Sign off'}
            </button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          Signing off locks the timeline, tickets, fuel and cost of this trip. Any later change
          needs an amendment approved by a second authorised user, and marks every report that
          includes the trip as amended.
        </p>
        <InlineError message={error} />
      </Modal>

      <Modal
        open={dialog === 'amend'}
        title={`Amend ${trip.reference}`}
        onClose={() => setDialog(null)}
        wide
      >
        <AmendmentForm
          trip={trip}
          busy={busy}
          error={error}
          onCancel={() => setDialog(null)}
          onSubmit={(body) =>
            run(
              () => requestTripAmendment(trip.id, body),
              'Amendment requested; a second user approves it.'
            )
          }
        />
      </Modal>

      <Modal
        open={!!dialog?.reject}
        title="Reject the amendment"
        onClose={() => setDialog(null)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setDialog(null)}
              className={buttonClasses.secondary}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() =>
                run(
                  () =>
                    rejectTripAmendment(dialog.reject.id, {
                      reason: rejectReason.trim() || undefined,
                    }),
                  'Amendment rejected; the signed-off figures stand.'
                )
              }
              disabled={busy}
              className={buttonClasses.danger}
            >
              Reject
            </button>
          </>
        }
      >
        <ReasonField value={rejectReason} onChange={setRejectReason} required={false} />
        <InlineError message={error} />
      </Modal>
    </section>
  );
}
